/**
 * Modal de transferência de mesa — move um pedido dine_in em andamento
 * para outra mesa (ação do garçom). Mesas ativas do tenant, sem a mesa
 * atual do pedido; a confirmação chama POST /orders/:id/transfer e as
 * telas conectadas (garçom/cozinha) recebem `order.transferred` via WS.
 */
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ordersApi, tables } from '../lib/api.js'
import type { OrderDetailed } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, Modal, Select, Spinner, toast } from './ui/index.js'

export function TransferTableModal({
  order,
  onClose,
  onSaved,
}: {
  order: OrderDetailed | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()
  const [targetId, setTargetId] = useState('')

  function tableLabel(number_: number, label: string | null): string {
    const mesa = t('admin.tables.mesa', { n: String(number_) })
    return label ? `${mesa} · ${label}` : mesa
  }

  const { data: allTables, isLoading } = useQuery({
    queryKey: ['tables'],
    queryFn: tables.list,
    enabled: order !== null,
  })

  // mesas ativas, sem a atual — transferir para onde já está é no-op
  const options = (allTables ?? [])
    .filter((tb) => tb.is_active && tb.id !== order?.table_id)
    .sort((a, b) => a.number - b.number)
    .map((tb) => ({ value: tb.id, label: tableLabel(tb.number, tb.label) }))

  const transferMutation = useMutation({
    mutationFn: (tableId: string) => ordersApi.transfer(order!.id, tableId),
    onSuccess: (res) => {
      toast.success(
        t('admin.transfer.moved_toast', {
          table: tableLabel(res.to_table_number, res.to_table_label),
        }),
      )
      onSaved()
      onClose()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.transfer.err')),
  })

  if (!order) return null

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.orders.transfer')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            disabled={!targetId || transferMutation.isPending}
            onClick={() => transferMutation.mutate(targetId)}
          >
            {transferMutation.isPending
              ? t('admin.transfer.transferring')
              : t('admin.transfer.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {t('admin.transfer.hint_start')}{' '}
          <strong className="text-gray-900">
            {tableLabel(order.table_number ?? 0, order.table_label)}
          </strong>{' '}
          {t('admin.transfer.hint_end')}
        </p>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner className="w-5 h-5 text-indigo-500" />
          </div>
        ) : options.length === 0 ? (
          <p className="text-sm text-amber-600">{t('admin.transfer.no_options')}</p>
        ) : (
          <Select
            label={t('admin.transfer.target')}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            options={options}
          />
        )}
      </div>
    </Modal>
  )
}
